import { useCallback, useEffect, useRef, useState } from 'react';
import type { Awareness } from 'y-protocols/awareness';
import type { Camera, Point } from '@os/engine';
import type { SceneStore } from '@os/doc';
import { presenceHz, roundHalf, type PresenceState, type PresenceUser } from '@os/shared';

/** What presence needs from the editor (EditorController implements it). */
export type PresenceSource = {
  readonly camera: Camera;
  subscribePointer(cb: (p: Point | null) => void): () => void;
  subscribeUi(cb: () => void): () => void;
  ui(): { selection: readonly string[] };
  subscribeCamera(cb: () => void): () => void;
  /** User navigation (wheel, pan, pinch, keys): stops following. */
  subscribeNavigate(cb: () => void): () => void;
  followCamera(c: Camera): void;
};

/** `follows`: the user id this peer follows, if any; `page`: the page it shows (null: an older build, the first page). */
export type Peer = { clientId: number; user: PresenceUser; cursor: [number, number] | null; selection: string[]; follows: string | null; page: string | null };

/** The page side of the board (P14): which page this tab shows, and how following a peer changes it. */
export type PresencePage = { current(): string; subscribe(cb: () => void): () => void; set(id: string): void };

/** The page a peer is on: its own `g` when that page exists, else the first page. */
export const peerPage = (peer: Pick<Peer, 'page'>, store: Pick<SceneStore, 'pages'>): string => {
  const pages = store.pages();
  return peer.page !== null && pages.some((p) => p.id === peer.page) ? peer.page : pages[0]!.id;
};

/** The peers on the page this store shows: only their cursors and selections are drawn (P14 Ruling 15). */
export const onPage = <T extends Pick<Peer, 'page'>>(peers: readonly T[], store: Pick<SceneStore, 'pages' | 'page'>): T[] => peers.filter((p) => peerPage(p, store) === store.page);

const NO_PEERS: Peer[] = [];

const peersOf = (aw: Awareness): Peer[] =>
  [...aw.getStates()]
    .filter(([id, s]) => id !== aw.clientID && (s as Partial<PresenceState>).u)
    .map(([clientId, s]) => {
      const st = s as PresenceState;
      return { clientId, user: st.u, cursor: st.p ?? null, selection: st.s ?? [], follows: typeof st.f === 'string' ? st.f : null, page: typeof st.g === 'string' ? st.g : null };
    })
    .sort((a, b) => a.user.n.localeCompare(b.user.n) || a.clientId - b.clientId);

/**
 * Publishes this tab's cursor (throttled to presenceHz, nothing at rest), selection and, only while someone
 * follows this user, viewport; reads the peers; follows a peer's viewport until the user navigates.
 */
export function usePresence(awareness: Awareness, source: PresenceSource, me: PresenceUser, opts: { economy?: boolean; off?: boolean; page?: PresencePage } = {}) {
  const [peers, setPeers] = useState<Peer[]>(() => peersOf(awareness));
  const [following, setFollowing] = useState<string | null>(null);
  /** Why following last stopped: the user moved the view themselves (the UI tells them). */
  const [stoppedBy, setStoppedBy] = useState<'navigation' | null>(null);
  const followingRef = useRef<string | null>(null);
  const economy = opts.economy ?? false;
  /** The room's quota from 90 % (P12): nothing published, no peer drawn (the room drops presence anyway). */
  const off = opts.off ?? false;
  const pageRef = useRef(opts.page);
  pageRef.current = opts.page;

  const follow = useCallback(
    (userId: string | null) => {
      followingRef.current = userId;
      setFollowing(userId);
      setStoppedBy(null);
      awareness.setLocalStateField('f', userId);
    },
    [awareness],
  );

  useEffect(() => {
    if (off) {
      if (followingRef.current) follow(null);
      awareness.setLocalState(null);
      return;
    }
    const page = () => pageRef.current?.current();
    awareness.setLocalState({ u: me, p: null, s: [...source.ui().selection], ...(page() ? { g: page() } : {}) } satisfies PresenceState);
    const states = () => [...awareness.getStates()].filter(([id]) => id !== awareness.clientID).map(([, s]) => s as Partial<PresenceState>);
    const interval = () => 1000 / presenceHz(Math.max(0, awareness.getStates().size - 1), economy);

    // Throttle with a trailing send: the last position always goes out, and nothing is sent at rest.
    const throttled = (send: () => void) => {
      let last = -Infinity;
      let timer: ReturnType<typeof setTimeout> | null = null;
      return {
        fire() {
          const wait = last + interval() - Date.now();
          if (wait <= 0) {
            last = Date.now();
            send();
          } else if (!timer) {
            timer = setTimeout(() => {
              timer = null;
              last = Date.now();
              send();
            }, wait);
          }
        },
        stop: () => timer && clearTimeout(timer),
      };
    };

    let pointer: Point | null = null;
    const cursor = throttled(() => awareness.setLocalStateField('p', pointer ? [roundHalf(pointer.x), roundHalf(pointer.y)] : null));
    const followedByAnyone = () => states().some((s) => s.f === me.id);
    // Economy (P12, quota at 70 %): no viewport for followers, only cursors at 2 Hz.
    const viewport = throttled(() => {
      const c = source.camera;
      awareness.setLocalStateField('v', followedByAnyone() && !economy ? [c.x, c.y, c.z] : undefined);
    });

    let selection = source.ui().selection;
    let lastFollowedV: string | null = null;
    const onAwareness = () => {
      setPeers(peersOf(awareness));
      // Someone started or stopped following me: publish or withdraw the viewport.
      const published = (awareness.getLocalState() as PresenceState | null)?.v !== undefined;
      if ((followedByAnyone() && !economy) !== published) viewport.fire();
      // I follow someone: put my camera on their viewport; they left the board: following ends quietly.
      const target = followingRef.current;
      if (target && !states().some((s) => s.u?.id === target)) {
        follow(null);
        lastFollowedV = null;
        return;
      }
      // The followed person is on another page: go there first, then take their viewport again (P14).
      const theirs = target ? states().find((s) => s.u?.id === target)?.g : undefined;
      if (typeof theirs === 'string' && pageRef.current && theirs !== pageRef.current.current()) {
        pageRef.current.set(theirs);
        lastFollowedV = null;
      }
      const v = target ? states().find((s) => s.u?.id === target && s.v)?.v : undefined;
      if (v && JSON.stringify(v) !== lastFollowedV) {
        lastFollowedV = JSON.stringify(v);
        source.followCamera({ x: v[0], y: v[1], z: v[2] });
      }
    };
    awareness.on('change', onAwareness);
    const offs = [
      source.subscribePointer((p) => {
        pointer = p;
        cursor.fire();
      }),
      source.subscribeUi(() => {
        const next = source.ui().selection;
        if (next === selection) return;
        selection = next;
        awareness.setLocalStateField('s', [...next]);
      }),
      source.subscribeCamera(() => {
        if (followedByAnyone() && !economy) viewport.fire();
      }),
      ...(pageRef.current
        ? [
            pageRef.current.subscribe(() => {
              // Going to another page than the followed person's is leaving them, like moving the view (checked
              // first: publishing `g` runs the awareness handler, which would take this tab back to their page).
              const theirs = followingRef.current ? states().find((s) => s.u?.id === followingRef.current)?.g : undefined;
              if (typeof theirs === 'string' && theirs !== page()) {
                follow(null);
                setStoppedBy('navigation');
              }
              awareness.setLocalStateField('g', page());
            }),
          ]
        : []),
      source.subscribeNavigate(() => {
        if (followingRef.current) {
          follow(null);
          setStoppedBy('navigation');
        }
        lastFollowedV = null;
      }),
    ];
    return () => {
      offs.forEach((off) => off());
      awareness.off('change', onAwareness);
      cursor.stop();
      viewport.stop();
      awareness.setLocalState(null);
    };
  }, [awareness, source, me, economy, off, follow]);

  const shown = off ? NO_PEERS : peers;
  const followers = shown.filter((p) => p.follows === me.id);
  return { peers: shown, following, follow, followers, stoppedBy, clearStopped: () => setStoppedBy(null) };
}
